/**
 * Roblox-inspired blocky builders: bevelled boxes with flat, saturated colours. No textures
 * except a tiny generated stud pattern for the baseplate.
 */
import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

export const DARK = '#263238';

const geoCache = new Map<string, THREE.BufferGeometry>();
export function boxGeo(w: number, h: number, d: number, r = 0.08): THREE.BufferGeometry {
  const k = `${w},${h},${d},${r}`;
  let g = geoCache.get(k);
  if (!g) {
    const radius = Math.min(r, w / 2, h / 2, d / 2);
    g = radius > 0 ? new RoundedBoxGeometry(w, h, d, 2, radius) : new THREE.BoxGeometry(w, h, d);
    geoCache.set(k, g);
  }
  return g;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
export function mat(hex: string): THREE.MeshLambertMaterial {
  let m = matCache.get(hex);
  if (!m) {
    m = new THREE.MeshLambertMaterial({ color: new THREE.Color(hex) });
    matCache.set(hex, m);
  }
  return m;
}

export function box(w: number, h: number, d: number, colour: string, x = 0, y = 0, z = 0, r = 0.08): THREE.Mesh {
  const m = new THREE.Mesh(boxGeo(w, h, d, r), mat(colour));
  m.position.set(x, y, z);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

export function buildTree(rng: () => number): THREE.Object3D {
  const g = new THREE.Group();
  const h = 1.5 + rng() * 2;
  const greens = ['#43a047', '#2e7d32', '#66bb6a', '#7cb342'];
  g.add(box(0.7, h, 0.7, '#6d4c41', 0, h / 2, 0, 0.05));
  const c1 = 2 + rng() * 1.5;
  g.add(box(c1, c1, c1, greens[Math.floor(rng() * greens.length)]!, 0, h + c1 / 2 - 0.3, 0, 0.15));
  if (rng() > 0.5) {
    const c2 = c1 * 0.6;
    g.add(box(c2, c2, c2, greens[Math.floor(rng() * greens.length)]!, 0, h + c1 + c2 / 2 - 0.6, 0, 0.12));
  }
  return g;
}

let studTexture: THREE.CanvasTexture | null = null;
export function studTex(): THREE.CanvasTexture {
  if (studTexture) return studTexture;
  const c = document.createElement('canvas');
  c.width = c.height = 64;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#5ab84a';
  ctx.fillRect(0, 0, 64, 64);
  ctx.fillStyle = '#4a9d3d';
  ctx.beginPath();
  ctx.arc(32, 35, 17, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#6cc75a';
  ctx.beginPath();
  ctx.arc(32, 31, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#7fd66c';
  ctx.beginPath();
  ctx.arc(32, 29, 10, 0, Math.PI * 2);
  ctx.fill();
  studTexture = new THREE.CanvasTexture(c);
  studTexture.wrapS = studTexture.wrapT = THREE.RepeatWrapping;
  studTexture.repeat.set(400, 400);
  studTexture.magFilter = THREE.NearestFilter;
  studTexture.colorSpace = THREE.SRGBColorSpace;
  studTexture.anisotropy = 4;
  return studTexture;
}

/** Roblox-ish saturated kit palette. First six are distinct under common colour blindness. */
export const KIT_COLOURS: { name: string; hex: string }[] = [
  { name: 'Bright red', hex: '#e53935' },
  { name: 'Bright blue', hex: '#1e88e5' },
  { name: 'Bright yellow', hex: '#fdd835' },
  { name: 'Lime', hex: '#7cb342' },
  { name: 'Orange', hex: '#fb8c00' },
  { name: 'Purple', hex: '#8e24aa' },
  { name: 'Teal', hex: '#00acc1' },
  { name: 'Pink', hex: '#ec407a' },
  { name: 'White', hex: '#f5f5f5' },
  { name: 'Black', hex: '#263238' },
];
export const DEFAULT_KIT = KIT_COLOURS[1]!.hex;
export function isKitColour(hex: string): boolean {
  return KIT_COLOURS.some((c) => c.hex === hex);
}
